import { useSelector } from 'react-redux';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { GetSwipePaymentPlansAPI } from 'Services';
import { IAPI_ACTION, IDispatcherType, INITIAL_STATE } from 'types';
import Toasty from 'components/atoms/Toasty';
import { loadState } from 'utils/LocalStorage';

export const setAPIError = (state: ReturnType<typeof INITIAL_STATE>, action: IAPI_ACTION): void => {
	state.loading = false;
	state.error = action.error.message || 'Something went wrong';
	Toasty.error(state.error);
};

export const SwipePaymentPlans = createAsyncThunk('get/swipePaymentPlans', GetSwipePaymentPlansAPI);

const getPlanPriority = (data: any) => {
	const name = data?.device?.name;
	switch (name) {
		case 'BharatPe_One_P12':
			return 0;
		case 'BharatPe_Neo':
			return 1;
		case 'Android_Ingenico':
			return 2;
		case 'Old_Payswiff_4G':
			return 3;
		case 'BharatPe_Morefun':
			return 4;
		case 'Payswiff_Wi-Fi':
			return 5;
		default:
			return 100;
	}
};

const sortPlans = (plansArray: any, deviceInfo: any) => {
	const { name } = deviceInfo;
	if (plansArray.length < 2 || name === 'Old_Payswiff_4G' || name === 'BharatPe_One_P12') return plansArray;
	const newArray: any = [];
	if (plansArray[1]?.type.toLowerCase() === 'rental' && plansArray.length < 3) {
		newArray.push(plansArray[1]);
		newArray.push(plansArray[0]);
		return newArray;
	}
	return plansArray;
};

export const SwipePaymentPlansSlice = createSlice({
	name: 'swipePaymentPlans',
	initialState: INITIAL_STATE(),
	reducers: {
		CLEAR_ERROR: (state) => {
			state.error = '';
		},
		RESET: (state) => Object.assign(state, INITIAL_STATE()),
	},
	extraReducers: {
		[SwipePaymentPlans.pending.toString()]: (state: ReturnType<typeof INITIAL_STATE>) => {
			state.data = {};
			state.error = '';
			state.loading = true;
			state.isFetched = false;
		},
		[SwipePaymentPlans.rejected.toString()]: setAPIError,
		[SwipePaymentPlans.fulfilled.toString()]: (state: ReturnType<typeof INITIAL_STATE>, action: IAPI_ACTION<any>) => {
			const { payload } = action;
			state.loading = false;
			state.isFetched = true;
			state.error = '';
			if (payload?.device_plans && payload?.device_plans.length > 1) {
				payload?.device_plans.sort(function (a: any, b: any): any {
					return getPlanPriority(a) - getPlanPriority(b);
				});
			}
			const isSortPlansData = false;
			if (isSortPlansData) {
				const newPayload: any = { ...payload };
				const devicePlans = payload?.device_plans;
				const newDevicePlans = [];
				for (let i = 0; i < devicePlans.length; i = i + 1) {
					const newArray: any = { ...devicePlans[i] };
					const plansArray = devicePlans[i]?.plans;
					newArray.plans = sortPlans(plansArray, devicePlans[i]?.device);
					newDevicePlans.push(newArray);
				}
				newPayload.device_plans = newDevicePlans;
				state.data = newPayload;
			} else {
				state.data = payload;
			}
		},
	},
});

interface IStates {
	[SwipePaymentPlansSlice.name]: ReturnType<typeof SwipePaymentPlansSlice.reducer>;
}

export const swipePaymentPlansSelector = (state: IStates): any => {
	return state[SwipePaymentPlansSlice.name] || {};
};

export const useSwipePaymentPlans = (): any => useSelector(swipePaymentPlansSelector);

export const getSwipePaymentPlans =
	(params: any) =>
	async (dispatch: IDispatcherType): Promise<void> => {
		await dispatch(SwipePaymentPlans(params));
		return;
	};

export const resetSwipePaymentPlans =
	() =>
	async (dispatch: IDispatcherType): Promise<void> => {
		await dispatch(SwipePaymentPlansSlice.actions.RESET());
		return;
	};
